import { supabase } from "@/integrations/supabase/client";
import { storageKeyFor, type LocaleCode } from "@/components/admin/LocaleTabs";

export const AUTO_LOCALES: LocaleCode[] = ["pt-BR", "en", "es", "fr", "it"];

const HASH_KEY = "cms_translation_hashes";

/** Small stable hash of the source (pt-PT) text. */
export const hashText = (text: string) => {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return `${h}:${text.length}`;
};

export type HashMap = Record<string, string>;

export const loadHashes = async (): Promise<HashMap> => {
  const { data } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", HASH_KEY)
    .maybeSingle();
  let v: any = data?.value;
  if (typeof v === "string") { try { v = JSON.parse(v); } catch { v = {}; } }
  return v && typeof v === "object" ? (v as HashMap) : {};
};

export const saveHashes = async (hashes: HashMap) => {
  await supabase.from("app_settings").upsert({ key: HASH_KEY, value: hashes as any }, { onConflict: "key" });
};

export interface TranslateResult {
  translatedFields: number;
  skipped: number;
  failedLocales: string[];
}

/**
 * Translates the given pt-PT fields into all secondary locales.
 * Fields whose source text hash is unchanged (and already has a stored
 * translation) are skipped, so no AI tokens are wasted.
 */
export const translateFields = async (
  fields: { key: string; value: string }[],
  opts: {
    force?: boolean;
    existing?: Record<string, string>;
    onValue?: (storageKey: string, value: string) => void;
    onProgress?: (locale: LocaleCode, index: number, total: number) => void;
  } = {},
): Promise<TranslateResult> => {
  const source = fields.filter((f) => (f.value || "").trim());
  if (!source.length) return { translatedFields: 0, skipped: 0, failedLocales: [] };

  const hashes = await loadHashes();
  const failedLocales: string[] = [];
  let translatedFields = 0;
  let skipped = 0;
  let changed = false;

  for (let li = 0; li < AUTO_LOCALES.length; li++) {
    const locale = AUTO_LOCALES[li];
    opts.onProgress?.(locale, li, AUTO_LOCALES.length);

    const pending = source.filter((f) => {
      if (opts.force) return true;
      const sKey = storageKeyFor(f.key, locale);
      const hasValue = opts.existing ? Boolean((opts.existing[sKey] || "").trim()) : true;
      const upToDate = hashes[sKey] === hashText(f.value) && hasValue;
      if (upToDate) skipped++;
      return !upToDate;
    });

    if (!pending.length) continue;

    // Chunk to keep prompts small and reliable.
    const chunkSize = 12;
    for (let i = 0; i < pending.length; i += chunkSize) {
      const chunk = pending.slice(i, i + chunkSize);
      try {
        const { data, error } = await supabase.functions.invoke("translate", {
          body: { texts: chunk, targetLang: locale },
        });
        if (error) throw error;
        // Provider busy: don't save untranslated originals as translations
        if (data?.fallback) throw new Error(data.warning || "translation unavailable");
        const translated: { key: string; value: string }[] = data?.translated || [];
        if (!translated.length) throw new Error("empty translation");

        const updates = translated
          .filter((t) => (t.value || "").trim())
          .map((t) => ({ key: storageKeyFor(t.key, locale), value: t.value as any }));

        if (updates.length) {
          const { error: upErr } = await supabase
            .from("app_settings")
            .upsert(updates, { onConflict: "key" });
          if (upErr) throw upErr;
          updates.forEach((u, idx) => {
            opts.onValue?.(u.key, translated[idx].value);
            const src = chunk.find((c) => storageKeyFor(c.key, locale) === u.key);
            if (src) { hashes[u.key] = hashText(src.value); changed = true; }
          });
          translatedFields += updates.length;
        }
      } catch (e) {
        console.error(`translate ${locale} failed`, e);
        if (!failedLocales.includes(locale)) failedLocales.push(locale);
      }
      await new Promise((r) => setTimeout(r, 250));
    }
  }

  if (changed) await saveHashes(hashes);

  return { translatedFields, skipped, failedLocales };
};
